import { Component, OnInit, signal } from '@angular/core';
import { Business, BusinessEntityType, SiteProperties } from '../../../models/business';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { CountryProxyService } from '../../../proxies/country.proxy.service';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BreadcrumbService, ModalService } from '@card-software/library';
import { LotsIdModalComponent } from '../../shared/modals/lots-id-modal/lots-id-modal.component';
import { Representative } from '../../../models/representative/representative';
import { Address } from '../../../models/address/address';
import { ClientDetails } from '../../../models/client/client-details';
import { MessageService } from 'primeng/api';
import _ from 'lodash';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { BusinessRoles } from '../../../models/roles/roles-group';
import { Site } from '@card-software/library';
import { SiteService } from '@card-software/nexus-utilities';

export interface Tab {
  label: string;
  value: number;
}

/**
 * A role this business can hold, surfaced as a card under the general information (#4892). Each role owns
 * settings too large to sit as a tab beside the addresses, so the card is the way into them.
 *
 * <p>Only the roles the system can actually assign appear — the ones in {@link BusinessEntityType}. The
 * mockup's third card, Carrier, has no business type behind it, so it is left out rather than rendered as a
 * control that can never do anything.</p>
 */
export interface BusinessRoleCard {
  key: 'provider' | 'client';
  title: string;
  icon: string;
  description: string;
  /** The business holds this role on the current site. */
  assigned: boolean;
  /** Whether the card's link can be followed — an unsaved business has no id to open a role page with. */
  enabled: boolean;
  linkLabel: string;
}

@Component({
  selector: 'app-business-details',
  templateUrl: './business-details.component.html',
})
export class BusinessDetailsComponent implements OnInit {
  editing = signal(false);
  originalBusiness = signal<Business | null>(null);
  addingNewLot = signal(false);
  isReadOnly = false;
  canAssignSites = false;
  value = 0;

  // Provider details left this page for one of their own (#4892) — the Provider role card is the way in now.
  tabs = signal<Tab[]>([
    { label: 'Addresses', value: 0 },
    { label: 'Representatives', value: 1 },
    { label: 'Client Details', value: 3 },
    { label: 'Lots', value: 4 },
    { label: 'Tax Authorizations', value: 5 },
  ]);

  representatives: Representative[] = [];
  addresses: Address[] = [];
  client: ClientDetails | null = null;
  lotIds = new FormArray<FormControl<string>>([]);

  selectTab = new FormControl<number>(0);
  nameBusiness = new FormControl<string | null>(null, [Validators.required]);
  taxIndicationCodeBusiness = new FormControl<string | null>(null, [Validators.required]);
  /** Where the business is (#5121). Required: the API rejects a save without it, and every existing row
   *  was backfilled, so there is no such thing as a business whose country is unknown. */
  countryCodeBusiness = new FormControl<string | null>(null, [Validators.required]);
  readonly countryOptions = signal<{ label: string; value: string }[]>([]);
  businessType = new FormControl<number[] | null>(null, [Validators.required]);
  businessTypeSuggestions = signal<{ label: string; value: number }[]>([]);

  siteIdsControl = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [BusinessDetailsComponent.atLeastOneSiteValidator],
  });
  siteBusinessTypesArray = new FormArray<FormGroup>([]);
  expandedSiteIds: string[] = [];
  siteBusinessTypeGroups: { siteId: string; formGroup: FormGroup }[] = [];

  get availableSites(): Site[] {
    return SiteService.getSites();
  }

  get tabsToDisplay() {
    return this.tabs().filter((tab) => {
      const selectedTypes = this.aggregatedBusinessTypes;
      if (tab.value === 3 && !selectedTypes.includes(BusinessEntityType.Client)) {
        return false;
      }
      // Tax authorizations (#4791) belong to a counterparty we trade fiscal documents with — a provider
      // whose invoice numbers we validate, or a client we invoice. Anything else has none to register.
      //
      // A provider's authorizations moved onto the Provider Details page (#4892), so the tab would be a
      // second window onto the same records. It stays only for a business that is a client and NOT a
      // provider — otherwise a client-only counterparty would have nowhere left to register one.
      if (tab.value === 5 && !(selectedTypes.includes(BusinessEntityType.Client) && !selectedTypes.includes(BusinessEntityType.Provider))) {
        return false;
      }
      return true;
    });
  }

  /**
   * The role cards under the general information. Provider opens its own page; Client still edits in a tab,
   * so its card jumps there rather than navigating.
   */
  get roleCards(): BusinessRoleCard[] {
    const selectedTypes = this.aggregatedBusinessTypes;
    const isSaved = !!this.originalBusiness()?.id;

    const providerAssigned = selectedTypes.includes(BusinessEntityType.Provider);
    const clientAssigned = selectedTypes.includes(BusinessEntityType.Client);

    return [
      {
        key: 'provider',
        title: 'Provider',
        icon: 'pi pi-truck',
        description: 'Classification, purchasing, payment terms, payable bank accounts and tax treatment.',
        assigned: providerAssigned,
        // The role is granted here; its page only opens once that grant has actually been saved.
        enabled: providerAssigned && isSaved,
        linkLabel: providerAssigned ? 'Open Provider Details' : 'Assign role',
      },
      {
        key: 'client',
        title: 'Customer',
        icon: 'pi pi-shopping-bag',
        description: 'Sales terms, credit control and receivable accounts.',
        assigned: clientAssigned,
        enabled: clientAssigned,
        linkLabel: clientAssigned ? 'Open Client Details' : 'Assign role',
      },
    ];
  }

  openRole(card: BusinessRoleCard): void {
    if (!card.enabled) return;

    if (card.key === 'provider') {
      this.router.navigate(['/business', this.originalBusiness()?.id, 'provider-details']);
      return;
    }
    this.selectTab.setValue(3);
  }

  get aggregatedBusinessTypes(): number[] {
    if (!this.canAssignSites) {
      return this.businessType.value ?? [];
    }
    const set = new Set<number>();
    for (const group of this.siteBusinessTypeGroups) {
      const types = (group.formGroup.get('businessTypes')?.value ?? []) as number[];
      for (const t of types) set.add(Number(t));
    }
    return Array.from(set);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private businessProxyService: BusinessProxyService,
    private modalService: ModalService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private enumService: EnumService,
    private userAuthenticationService: UserAuthenticationService,
    private countryProxyService: CountryProxyService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.getTypeBusinessEnum();
    this.loadCountries();
  }

  /**
   * The country picker's options, from the API's catalog rather than a list kept here. The provider screen
   * hardcodes six Central American countries, which is why a supplier in Italy or South Korea has nowhere
   * to be filed; this asks the same source the API validates against.
   */
  private loadCountries(): void {
    this.countryProxyService.getAll().subscribe({
      next: (response) =>
        this.countryOptions.set(
          (response.data ?? []).map((country) => ({ label: `${country.name} (${country.code})`, value: country.code })),
        ),
      error: () => this.countryOptions.set([]),
    });
  }

  ngOnInit(): void {
    const userRoles = this.userAuthenticationService.roles;
    this.isReadOnly = !userRoles.includes(BusinessRoles.BusinessAdmin);
    this.canAssignSites = userRoles.includes(BusinessRoles.FinanceSiteAssigner);

    this.siteIdsControl.valueChanges.subscribe((ids) => {
      this.syncSiteBusinessTypes(ids ?? []);
    });

    if (!this.canAssignSites) {
      const selected = SiteService.getSelectedSiteId();
      this.siteIdsControl.setValue(selected ? [selected] : []);
      this.siteIdsControl.disable();
    }

    // The business id moved into the path (#4892: business/{id}); the query parameter is still read so
    // links minted before the move keep working.
    this.activatedRoute.paramMap.subscribe((params) => {
      const id = params.get('id') ?? this.activatedRoute.snapshot.queryParamMap.get('id');
      if (id) {
        if (id === 'new') {
          this.editing.set(false);
          this.breadcrumbService.setCurrent('Common_New');
        } else {
          this.getDataBusiness(id);
          this.editing.set(true);
        }
      }
    });
  }

  private static atLeastOneSiteValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return Array.isArray(value) && value.length > 0 ? null : { required: true };
  }

  private buildSiteBusinessTypeGroup(siteId: string, businessTypes: number[] = []): FormGroup {
    return new FormGroup({
      siteId: new FormControl(siteId, { nonNullable: true }),
      businessTypes: new FormControl<number[]>(businessTypes, { nonNullable: true }),
    });
  }

  private syncSiteBusinessTypes(currentSiteIds: string[]): void {
    const existingBySiteId = new Map<string, FormGroup>();
    for (const group of this.siteBusinessTypesArray.controls) {
      const siteId = group.get('siteId')?.value;
      if (siteId) existingBySiteId.set(siteId, group);
    }

    const sourceProps = this.originalBusiness()?.siteProperties ?? [];

    this.siteBusinessTypesArray.clear({ emitEvent: false });
    const groups: { siteId: string; formGroup: FormGroup }[] = [];
    for (const siteId of currentSiteIds) {
      const existing = existingBySiteId.get(siteId);
      if (existing) {
        this.siteBusinessTypesArray.push(existing, { emitEvent: false });
        groups.push({ siteId, formGroup: existing });
        continue;
      }
      const fromBusiness = sourceProps.find((sp) => sp.siteId === siteId)?.businessTypes ?? [];
      const newGroup = this.buildSiteBusinessTypeGroup(siteId, [...fromBusiness]);
      this.siteBusinessTypesArray.push(newGroup, { emitEvent: false });
      groups.push({ siteId, formGroup: newGroup });
    }

    this.siteBusinessTypeGroups = groups;
    this.expandedSiteIds = [...currentSiteIds];
  }

  getSiteName(siteId: string): string {
    return this.availableSites.find((s) => s.id === siteId)?.name ?? siteId;
  }

  getDataBusiness(id: string) {
    this.businessProxyService.getBusinessById(id).subscribe(({ data }) => {
      if (data) {
        this.editing.set(true);
        this.originalBusiness.set(data);
        this.breadcrumbService.setCurrent(data.code);
        this.populatesControls(data);
        this.client = data.clientDetails!;
      }
    });
  }

  populatesControls(business: Business) {
    this.nameBusiness.setValue(business.name ?? null);
    this.taxIndicationCodeBusiness.setValue(business.taxIndicatorCode ?? null);
    this.countryCodeBusiness.setValue(business.countryCode ?? null);
    this.businessType.setValue(business.businessTypes ?? []);
    this.addresses = business.addresses ?? [];
    this.representatives = business.representatives ?? [];
    this.client = business.clientDetails ?? null;
    this.lotIds.clear();
    business.lotNumbers?.forEach((lotId) => {
      this.lotIds.push(new FormControl(lotId, { nonNullable: true }));
    });

    if (this.canAssignSites) {
      const ids = business.siteIds ?? [];
      this.siteIdsControl.setValue(ids.length > 0 ? ids : [SiteService.getSelectedSiteId() ?? ''].filter(Boolean));
    } else {
      const currentSiteId = SiteService.getSelectedSiteId() ?? '';
      this.siteIdsControl.setValue(currentSiteId ? [currentSiteId] : []);
      this.syncSiteBusinessTypes(currentSiteId ? [currentSiteId] : []);
    }
  }

  cancel(): void {
    this.editing.set(false);
    this.originalBusiness.set(null);
    this.router.navigate(['/businesses']);
  }

  save(): void {
    if (_.isNil(this.nameBusiness.value)) {
      GlobalMessageUtil.warn('Business Name is required.', 'Please provide a valid business name.');
      return;
    }

    let siteProperties: SiteProperties[];
    let payloadSiteIds: string[] | undefined;
    let aggregatedTypes: number[];

    if (this.canAssignSites) {
      if (this.siteIdsControl.invalid) {
        GlobalMessageUtil.warn('Sites are required.', 'At least one site must be selected.');
        return;
      }
      const formSiteIds = this.siteIdsControl.value ?? [];
      siteProperties = formSiteIds.map((siteId) => {
        const group = this.siteBusinessTypeGroups.find((g) => g.siteId === siteId);
        const types = ((group?.formGroup.get('businessTypes')?.value ?? []) as number[]).map((v) => Number(v));
        return { siteId, businessTypes: types };
      });
      payloadSiteIds = formSiteIds;
      aggregatedTypes = this.aggregatedBusinessTypes;
    } else {
      const selectedTypes = this.businessType.value ?? [];
      const currentSiteId = SiteService.getSelectedSiteId() ?? '';
      const otherSiteProperties = (this.originalBusiness()?.siteProperties ?? []).filter((sp) => sp.siteId !== currentSiteId);
      siteProperties = [...otherSiteProperties, { siteId: currentSiteId, businessTypes: selectedTypes }];
      payloadSiteIds = undefined;
      aggregatedTypes = selectedTypes;
    }

    if (aggregatedTypes.length === 0) {
      GlobalMessageUtil.warn('Business Type is required.', 'Please select at least one business type.');
      return;
    }

    const business: Business = {
      id: this.originalBusiness()?.id ?? '',
      name: this.nameBusiness.value,
      aliases: ['string'],
      code: this.originalBusiness()?.code ?? undefined,
      taxIndicatorCode: this.taxIndicationCodeBusiness.value ?? '',
      countryCode: this.countryCodeBusiness.value ?? undefined,
      addresses: this.addresses,
      representatives: this.representatives ?? [],
      leadAssociateUserName: this.originalBusiness()?.leadAssociateUserName ?? '',
      clientDetails: this.client ?? undefined,
      // Provider details are not sent from here any more (#4892). They are edited on their own page and
      // saved through PATCH business/{id}/provider-details; the API reads the omission as "unchanged" and
      // keeps what is stored. Echoing a copy loaded minutes ago would undo whatever that page just wrote.
      lotNumbers: this.lotIdValues,
      businessTypes: aggregatedTypes,
      siteProperties,
      ...(payloadSiteIds ? { siteIds: payloadSiteIds } : {}),
    };

    if (!this.editing()) {
      this.businessProxyService.createBusiness(business).subscribe({
        next: (result) => {
          if (result.success) {
            GlobalMessageUtil.success(
              this.translateService.instant('Business_Details_Created_Title'),
              this.translateService.instant('Business_Details_Created', {
                name: result.data?.name,
                code: result.data?.code,
              }),
            );
            this.router.navigate(['/businesses']);
          }
        },
      });
    } else {
      this.businessProxyService.updateBusiness(this.originalBusiness()?.id ?? '', business).subscribe({
        next: (result) => {
          if (result.success) {
            GlobalMessageUtil.success(
              this.translateService
                .instant('Business_Details_Update_Title')
                .replace('{{name}}', business.name ?? '')
                .replace('{{code}}', business.code ?? ''),
              this.translateService
                .instant('Business_Details_Update')
                .replace('{{name}}', business.name ?? '')
                .replace('{{code}}', business.code ?? ''),
            );
            this.router.navigate(['/businesses']);
          }
        },
      });
    }
  }

  setClient(client: ClientDetails) {
    this.client = client;
  }

  //#region handle lots

  get lotIdValues(): string[] {
    return this.lotIds.controls.map((control) => control.value);
  }

  addLotId(lotId: string): void {
    if (lotId.trim()) {
      this.lotIds.push(new FormControl(lotId.trim(), { nonNullable: true }));
      this.addingNewLot.set(false);
    }
  }

  openLotsIdModal(): void {
    this.modalService.open(LotsIdModalComponent, {}, {}).subscribe((result) => {
      if (result?.action === 'save' && result.data) {
        this.addLotId(result.data);
      }
    });
  }

  //#endregion

  //#region Business Type Enum

  getTypeBusinessEnum() {
    const enumValues = this.enumService.getEnum('BusinessEntityType').values;

    const mapped = Object.entries(enumValues).map(([key, label]) => ({
      label,
      value: Number(key),
    }));
    this.businessTypeSuggestions.set(mapped);
  }

  //#endregion
}
